import { useState, useRef, useEffect } from "react";
import { formatAddress } from "../lib/address";

export default function AddressInput({ value, onChange, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Defense-in-depth dismissal: any click/tap outside this component, or the Escape key,
  // closes the suggestion list. Without this, a list left open sits on top of the form fields
  // directly below the address box (Beds/Baths/Sqft/Price) and swallows clicks meant for them.
  useEffect(() => {
    if (!showDropdown) return;
    const onDocPointer = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDropdown(false); };
    const onKey = (e) => { if (e.key === "Escape") setShowDropdown(false); };
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("touchstart", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [showDropdown]);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 3) { setSuggestions([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&addressdetails=1&limit=5&countrycodes=us`, {
          headers: { 'Accept': 'application/json' }
        });
        const data = await res.json();
        // The fetch is debounced + async, so by the time it resolves the user may have already
        // tabbed/clicked on to the next field. Don't resurrect the dropdown over the form in that
        // case — once the address box is blurred its onBlur can't fire again to close it, so an
        // orphaned list would stay parked on top of Beds/Sqft/Price and block typing there.
        if (document.activeElement !== inputRef.current) { setShowDropdown(false); return; }
        setSuggestions(data);
        setShowDropdown(data.length > 0);
      } catch { setShowDropdown(false); }
    }, 400);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
        name="property-address-search"
        style={{ width: '100%' }}
      />
      {showDropdown && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: '#161616', border: '1px solid #2a2a2a',
          borderRadius: '0 0 7px 7px', zIndex: 1000, maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {suggestions.map((s, i) => (
            <div key={i}
              onMouseDown={() => { const formatted = s.address ? formatAddress(s.address) : ''; onChange(formatted && formatted.length > 5 ? formatted : s.display_name.slice(0, 60)); setShowDropdown(false); }}
              style={{
                padding: '10px 12px', cursor: 'pointer',
                fontSize: '13px', color: '#e8e4dd',
                borderBottom: '1px solid #1f1f1f'
              }}
              onMouseEnter={e => e.target.style.background = '#1f1f1f'}
              onMouseLeave={e => e.target.style.background = 'transparent'}
            >
              {s.display_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
